import {
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Switch,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { GlobalContext } from 'providers/Global';
import { ModalType, SCREEN_MD } from '../../utils/constants';
import { NetworkConfig } from '../../signing/types';
import {
  saveConfig,
  getAllConfigs,
  generateNetworkId,
  getNetworkDisplayName,
} from '../../utils/networkConfigStore';
import { validateNetworkConfigJson } from '../../utils/networkConfigValidator';
import { createNetworkConfig } from '../../api/networkConfigAPI';
import HashBasedAvatar from '../Avatar';
import DropZone from '../DropZone';
import { KeystoreAuthContent } from './KeystoreAuthDrawer';

type DrawerView = 'list' | 'add' | 'edit' | 'auth' | 'connected';

interface StoredConfig extends NetworkConfig {
  networkId: string;
}

const PrivateNetworksDrawer = () => {
  const {
    modals: { [`${ModalType.PrivateNetworksDrawer}`]: modalState },
    closeModal,
    openModal,
    connection: { accountId: connectedAccountId, disconnectWallet },
    connectedNetwork,
  } = useContext(GlobalContext);

  const open = modalState?.open ?? false;
  const isMobile = useMediaQuery({ query: `(max-width: ${SCREEN_MD})` });
  const toast = useToast();

  const [view, setView] = useState<DrawerView>('list');
  const [configs, setConfigs] = useState<StoredConfig[]>([]);
  const [selectedNetwork, setSelectedNetwork] = useState<StoredConfig | null>(null);
  const [editingConfig, setEditingConfig] = useState<StoredConfig | null>(null);
  const [jsonInput, setJsonInput] = useState('');
  const [importError, setImportError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  const [showJsonImport, setShowJsonImport] = useState(false);

  // Setup form state
  const [formName, setFormName] = useState('');
  const [formNodes, setFormNodes] = useState<{ endpoint: string; accountId: string }[]>([
    { endpoint: '', accountId: '' },
  ]);
  const [formMirrorNode, setFormMirrorNode] = useState('');
  const [formExplorer, setFormExplorer] = useState('');
  const [formRpcRelay, setFormRpcRelay] = useState('');
  const [formTls, setFormTls] = useState(false);
  const [formError, setFormError] = useState('');

  const loadConfigs = useCallback(() => {
    setConfigs(getAllConfigs());
  }, []);

  // Find the connected custom network config
  const connectedConfig = configs.find(
    c => connectedNetwork?.startsWith('custom-') && c.networkId === connectedNetwork,
  );

  useEffect(() => {
    if (open) {
      loadConfigs();
      // If already connected to a custom network, show the connected view
      if (connectedNetwork?.startsWith('custom-') && connectedAccountId) {
        setView('connected');
      } else {
        setView('list');
      }
    }
  }, [open, loadConfigs, connectedNetwork, connectedAccountId]);

  const handleClose = () => {
    closeModal(ModalType.PrivateNetworksDrawer);
    setView('list');
    resetForm();
    setEditingConfig(null);
  };

  const resetForm = () => {
    setJsonInput('');
    setImportError('');
    setImportFileName('');
    setFormName('');
    setFormNodes([{ endpoint: '', accountId: '' }]);
    setFormMirrorNode('');
    setFormExplorer('');
    setFormRpcRelay('');
    setFormTls(false);
    setFormError('');
    setShowJsonImport(false);
  };

  const openEditView = (config: StoredConfig) => {
    setEditingConfig(config);
    setFormName(config.name);
    setFormNodes(
      Object.entries(config.nodes).map(([endpoint, accountId]) => ({ endpoint, accountId })),
    );
    setFormMirrorNode(config.mirrorNode);
    setFormExplorer(config.explorer || '');
    setFormRpcRelay(config.rpcRelay || '');
    setFormTls(config.tls);
    setView('edit');
  };

  const handleEditSubmit = () => {
    if (!editingConfig) return;

    if (!formName.trim()) {
      setFormError('Network name is required');
      return;
    }
    const validNodes = formNodes.filter(n => n.endpoint.trim() && n.accountId.trim());
    if (validNodes.length === 0) {
      setFormError('At least one node is required');
      return;
    }
    if (!formMirrorNode.trim()) {
      setFormError('Mirror node URL is required');
      return;
    }

    const nodes: Record<string, string> = {};
    for (const node of validNodes) {
      nodes[node.endpoint.trim()] = node.accountId.trim();
    }

    const updatedConfig: StoredConfig = {
      ...editingConfig,
      name: formName.trim(),
      nodes,
      mirrorNode: formMirrorNode.trim(),
      explorer: formExplorer.trim() || null,
      rpcRelay: formRpcRelay.trim() || null,
      tls: formTls,
    };

    saveConfig(updatedConfig);
    loadConfigs();
    setView('list');
    resetForm();
    setEditingConfig(null);
    toast({ title: 'Network updated', status: 'success', duration: 3000 });
  };

  const handleJsonImport = async () => {
    setImportError('');
    const result = validateNetworkConfigJson(jsonInput);

    if (!result.valid) {
      setImportError(result.error);
      return;
    }

    setIsSubmitting(true);
    try {
      // Try saving to backend (requires auth)
      const response = await createNetworkConfig(result.config);
      if (response?.data) {
        saveConfig(response.data);
        loadConfigs();
        setView('list');
        resetForm();
        toast({ title: 'Network imported', status: 'success', duration: 3000 });
        setIsSubmitting(false);
        return;
      }
    } catch (err: any) {
      // If backend fails (auth, SSRF, etc.), log but continue to local save
      console.warn('Backend save failed, saving locally:', err?.response?.data?.message || err.message);
    }

    // Fallback: save to localStorage only (works without auth)
    try {
      const networkId = await generateNetworkId(result.config.nodes);
      saveConfig({ ...result.config, networkId });
      loadConfigs();
      setView('list');
      resetForm();
      toast({
        title: 'Network saved locally',
        description: 'Connect to a public network to sync with backend.',
        status: 'success',
        duration: 5000,
      });
    } catch (localErr: any) {
      setImportError(localErr.message || 'Failed to save network config');
    }
    setIsSubmitting(false);
  };

  const handleFileDrop = (file: File) => {
    setImportFileName(file.name);
    const reader = new FileReader();
    reader.onload = event => {
      const text = event.target?.result as string;
      setJsonInput(text);
      setImportError('');
    };
    reader.readAsText(file);
  };

  const handleSetupSubmit = async () => {
    setFormError('');

    if (!formName.trim()) {
      setFormError('Name is required');
      return;
    }

    const validNodes = formNodes.filter(n => n.endpoint.trim() && n.accountId.trim());
    if (validNodes.length === 0) {
      setFormError('At least one node endpoint is required');
      return;
    }

    if (!formMirrorNode.trim()) {
      setFormError('Mirror node URL is required');
      return;
    }

    const nodes: Record<string, string> = {};
    for (const node of validNodes) {
      nodes[node.endpoint.trim()] = node.accountId.trim();
    }

    const config: NetworkConfig = {
      version: 1,
      name: formName.trim(),
      nodes,
      mirrorNode: formMirrorNode.trim(),
      explorer: formExplorer.trim() || null,
      rpcRelay: formRpcRelay.trim() || null,
      tls: formTls,
    };

    setIsSubmitting(true);
    try {
      const response = await createNetworkConfig(config);
      if (response?.data) {
        saveConfig(response.data);
        loadConfigs();
        setView('list');
        resetForm();
        toast({ title: 'Network created', status: 'success', duration: 3000 });
        setIsSubmitting(false);
        return;
      }
    } catch (err: any) {
      console.warn('Backend save failed, saving locally:', err?.response?.data?.message || err.message);
    }

    try {
      const networkId = await generateNetworkId(config.nodes);
      saveConfig({ ...config, networkId });
      loadConfigs();
      setView('list');
      resetForm();
      toast({
        title: 'Network saved locally',
        description: 'Connect to a public network to sync with backend.',
        status: 'success',
        duration: 5000,
      });
    } catch (localErr: any) {
      setFormError(localErr.message || 'Failed to save network config');
    }
    setIsSubmitting(false);
  };

  const addNodeRow = () => {
    setFormNodes([...formNodes, { endpoint: '', accountId: '' }]);
  };

  const removeNodeRow = (index: number) => {
    if (formNodes.length <= 1) return;
    setFormNodes(formNodes.filter((_, i) => i !== index));
  };

  const updateNodeRow = (index: number, field: 'endpoint' | 'accountId', value: string) => {
    const updated = [...formNodes];
    updated[index] = { ...updated[index], [field]: value };
    setFormNodes(updated);
  };

  const renderConnectedView = () => {
    if (!connectedConfig) return null;
    const nodeEntries = Object.entries(connectedConfig.nodes);

    return (
      <VStack spacing={5} align="stretch">
        <Flex alignItems="center" gap={3}>
          <HashBasedAvatar UID={connectedConfig.networkId.slice(-5)} size={48} />
          <Box>
            <Text fontWeight={700} fontSize="lg">{connectedConfig.name}</Text>
            <Flex alignItems="center" gap={2}>
              <Box
                w={2} h={2} borderRadius="full" bg="green.400"
                sx={{
                  '@keyframes blink': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.3 },
                  },
                  animation: 'blink 2s ease-in-out infinite',
                }}
              />
              <Text fontSize="xs" color="green.600" fontWeight={500}>Connected</Text>
            </Flex>
          </Box>
        </Flex>

        <Box bg="gray.50" p={4} borderRadius={10}>
          <Text fontSize="xs" color="gray.500" fontWeight={600} mb={2} textTransform="uppercase">
            Account
          </Text>
          <Text fontSize="sm" fontWeight={600}>{connectedAccountId}</Text>
        </Box>

        <Box bg="gray.50" p={4} borderRadius={10}>
          <Text fontSize="xs" color="gray.500" fontWeight={600} mb={2} textTransform="uppercase">
            Network Details
          </Text>
          <VStack spacing={2} align="stretch">
            <Flex justifyContent="space-between">
              <Text fontSize="xs" color="gray.500">Nodes</Text>
              <Text fontSize="xs" fontWeight={500}>{nodeEntries.length}</Text>
            </Flex>
            {nodeEntries.map(([endpoint, nodeId]) => (
              <Flex key={endpoint} justifyContent="space-between">
                <Text fontSize="xs" color="gray.500">{nodeId}</Text>
                <Text fontSize="xs" fontWeight={500}>{endpoint}</Text>
              </Flex>
            ))}
            <Divider />
            <Flex justifyContent="space-between">
              <Text fontSize="xs" color="gray.500">Mirror Node</Text>
              <Text fontSize="xs" fontWeight={500} maxW="200px" isTruncated>{connectedConfig.mirrorNode}</Text>
            </Flex>
            {connectedConfig.explorer && (
              <Flex justifyContent="space-between">
                <Text fontSize="xs" color="gray.500">Explorer</Text>
                <Text fontSize="xs" fontWeight={500} maxW="200px" isTruncated>{connectedConfig.explorer}</Text>
              </Flex>
            )}
            <Flex justifyContent="space-between">
              <Text fontSize="xs" color="gray.500">TLS</Text>
              <Text fontSize="xs" fontWeight={500}>{connectedConfig.tls ? 'Enabled' : 'Disabled'}</Text>
            </Flex>
          </VStack>
        </Box>

        <Divider />

        <Flex gap={2}>
          <Button
            variant="outline"
            size="sm"
            flex={1}
            onClick={() => {
              setSelectedNetwork(connectedConfig);
              setView('auth');
            }}
          >
            Switch Account
          </Button>
          <Button
            variant="outline"
            size="sm"
            flex={1}
            onClick={() => setView('list')}
          >
            Switch Network
          </Button>
        </Flex>

        <Button
          variant="ghost"
          size="sm"
          color="red.500"
          _hover={{ bg: 'red.50' }}
          onClick={() => {
            disconnectWallet();
            handleClose();
          }}
        >
          Disconnect
        </Button>
      </VStack>
    );
  };

  const renderListView = () => (
    <VStack spacing={4} align="stretch">
      {configs.length === 0 ? (
        <Box textAlign="center" py={8}>
          <Text fontSize="lg" fontWeight={600} mb={2}>
            No Custom Networks
          </Text>
          <Text color="gray.500" mb={6}>
            Add a network to get started.
          </Text>
          <Button variant="primary" onClick={() => setView('add')} w="full">
            + Add Network
          </Button>
        </Box>
      ) : (
        <>
          {configs.map(config => (
            <Flex
              key={config.networkId}
              p={4}
              bg="white"
              border="1px"
              borderColor="gray.200"
              borderRadius={12}
              cursor="pointer"
              alignItems="center"
              gap={3}
              transition="all 0.15s"
              _hover={{ bg: 'gray.50', borderColor: 'gray.300', shadow: 'sm' }}
              onClick={() => {
                setSelectedNetwork(config);
                setView('auth');
              }}
            >
              <Box flexShrink={0}>
                <HashBasedAvatar UID={config.networkId.slice(-5)} size={40} />
              </Box>
              <Box flex={1} minW={0}>
                <Text fontWeight={600} fontSize="sm">{config.name}</Text>
                <Text fontSize="xs" color="gray.500">
                  {Object.keys(config.nodes).length} node(s)
                </Text>
              </Box>
              <IconButton
                aria-label="Edit network"
                icon={<Text fontSize="xs">✎</Text>}
                size="xs"
                variant="ghost"
                color="gray.400"
                _hover={{ color: 'gray.700', bg: 'gray.100' }}
                onClick={(e) => {
                  e.stopPropagation();
                  openEditView(config);
                }}
              />
              <Text fontSize="lg" color="gray.400">›</Text>
            </Flex>
          ))}
          <Divider />
          <Button size="sm" variant="primary" onClick={() => setView('add')} w="full">
            + Add Network
          </Button>
        </>
      )}
    </VStack>
  );

  const renderAddView = () => (
    <VStack spacing={4} align="stretch">
      <Button
        variant="ghost"
        size="sm"
        alignSelf="flex-start"
        onClick={() => {
          setView('list');
          resetForm();
        }}
      >
        ← Back
      </Button>
      <Text fontWeight={600} fontSize="lg">
        Add Network
      </Text>

      <FormControl isRequired>
        <FormLabel>Network Name</FormLabel>
        <Input
          value={formName}
          onChange={e => setFormName(e.target.value)}
          placeholder="My Private Network"
        />
      </FormControl>

      <FormControl isRequired>
        <FormLabel>Node Endpoints</FormLabel>
        {formNodes.map((node, i) => (
          <Flex key={i} gap={2} mb={2}>
            <Input
              placeholder="IP:Port (e.g. 10.0.1.5:50211)"
              value={node.endpoint}
              onChange={e => updateNodeRow(i, 'endpoint', e.target.value)}
              flex={1}
            />
            <Input
              placeholder="Account ID (e.g. 0.0.3)"
              value={node.accountId}
              onChange={e => updateNodeRow(i, 'accountId', e.target.value)}
              flex={1}
            />
            {formNodes.length > 1 && (
              <IconButton
                aria-label="Remove node"
                icon={<Text>✕</Text>}
                size="sm"
                variant="ghost"
                onClick={() => removeNodeRow(i)}
              />
            )}
          </Flex>
        ))}
        <Button size="sm" variant="ghost" onClick={addNodeRow}>
          + Add Node
        </Button>
      </FormControl>

      <FormControl isRequired>
        <FormLabel>Mirror Node URL</FormLabel>
        <Input
          value={formMirrorNode}
          onChange={e => setFormMirrorNode(e.target.value)}
          placeholder="https://mirror.example.com"
        />
      </FormControl>

      <FormControl>
        <FormLabel>Explorer URL (optional)</FormLabel>
        <Input
          value={formExplorer}
          onChange={e => setFormExplorer(e.target.value)}
          placeholder="https://explorer.example.com"
        />
      </FormControl>

      <FormControl>
        <FormLabel>RPC Relay URL (optional)</FormLabel>
        <Input
          value={formRpcRelay}
          onChange={e => setFormRpcRelay(e.target.value)}
          placeholder="https://rpc.example.com"
        />
      </FormControl>

      <FormControl display="flex" alignItems="center">
        <FormLabel mb={0}>TLS</FormLabel>
        <Switch isChecked={formTls} onChange={e => setFormTls(e.target.checked)} />
      </FormControl>

      {formError && (
        <Text color="red.500" fontSize="sm">
          {formError}
        </Text>
      )}

      <Button variant="primary" onClick={handleSetupSubmit} isLoading={isSubmitting}>
        Add Network
      </Button>

      <Divider />

      {/* Import from JSON — collapsible */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowJsonImport(!showJsonImport)}
      >
        {showJsonImport ? '▾ Hide JSON Import' : '▸ Import from JSON file'}
      </Button>

      {showJsonImport && (
        <VStack spacing={3} align="stretch" bg="gray.50" p={4} borderRadius={8}>
          <DropZone
            accept=".json"
            label="Drop config file here, or click to browse"
            sublabel="Accepts .json files"
            onFile={handleFileDrop}
            fileName={importFileName}
          />
          <Textarea
            placeholder="Or paste JSON config here..."
            value={jsonInput}
            onChange={e => {
              setJsonInput(e.target.value);
              setImportError('');
            }}
            rows={5}
            fontFamily="mono"
            fontSize="sm"
            bg="white"
          />
          {importError && (
            <Text color="red.500" fontSize="sm">
              {importError}
            </Text>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={handleJsonImport}
            isLoading={isSubmitting}
            isDisabled={!jsonInput.trim()}
          >
            Import Config
          </Button>
        </VStack>
      )}
    </VStack>
  );

  const renderEditView = () => (
    <VStack spacing={4} align="stretch">
      <Button
        variant="ghost"
        size="sm"
        alignSelf="flex-start"
        onClick={() => {
          setView('list');
          resetForm();
          setEditingConfig(null);
        }}
      >
        ← Back
      </Button>
      <Text fontWeight={600} fontSize="lg">
        Edit Network
      </Text>

      <FormControl isRequired>
        <FormLabel>Network Name</FormLabel>
        <Input
          value={formName}
          onChange={e => setFormName(e.target.value)}
          placeholder="My Private Network"
        />
      </FormControl>

      <FormControl isRequired>
        <FormLabel>Node Endpoints</FormLabel>
        {formNodes.map((node, i) => (
          <Flex key={i} gap={2} mb={2}>
            <Input
              placeholder="IP:Port"
              value={node.endpoint}
              onChange={e => updateNodeRow(i, 'endpoint', e.target.value)}
              flex={1}
            />
            <Input
              placeholder="Account ID"
              value={node.accountId}
              onChange={e => updateNodeRow(i, 'accountId', e.target.value)}
              flex={1}
            />
            {formNodes.length > 1 && (
              <IconButton
                aria-label="Remove node"
                icon={<Text>✕</Text>}
                size="sm"
                variant="ghost"
                onClick={() => removeNodeRow(i)}
              />
            )}
          </Flex>
        ))}
        <Button size="sm" variant="ghost" onClick={addNodeRow}>
          + Add Node
        </Button>
      </FormControl>

      <FormControl isRequired>
        <FormLabel>Mirror Node URL</FormLabel>
        <Input
          value={formMirrorNode}
          onChange={e => setFormMirrorNode(e.target.value)}
        />
      </FormControl>

      <FormControl>
        <FormLabel>Explorer URL (optional)</FormLabel>
        <Input
          value={formExplorer}
          onChange={e => setFormExplorer(e.target.value)}
        />
      </FormControl>

      <FormControl>
        <FormLabel>RPC Relay URL (optional)</FormLabel>
        <Input
          value={formRpcRelay}
          onChange={e => setFormRpcRelay(e.target.value)}
        />
      </FormControl>

      <FormControl display="flex" alignItems="center">
        <FormLabel mb={0}>TLS</FormLabel>
        <Switch isChecked={formTls} onChange={e => setFormTls(e.target.checked)} />
      </FormControl>

      {formError && (
        <Text color="red.500" fontSize="sm">{formError}</Text>
      )}

      <Button variant="primary" onClick={handleEditSubmit}>
        Save Changes
      </Button>
    </VStack>
  );

  return (
    <Drawer isOpen={open} placement="right" onClose={handleClose} size={isMobile ? 'full' : 'md'}>
      <DrawerOverlay backdropFilter="blur(10px)" />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>
          {view === 'connected' ? 'Custom Network'
            : view === 'auth' && selectedNetwork ? selectedNetwork.name
            : view === 'add' ? 'Add Network'
            : view === 'edit' ? 'Edit Network'
            : 'Custom Networks'}
        </DrawerHeader>
        <DrawerBody>
          {view === 'connected' && renderConnectedView()}
          {view === 'list' && renderListView()}
          {view === 'add' && renderAddView()}
          {view === 'edit' && renderEditView()}
          {view === 'auth' && selectedNetwork && (
            <KeystoreAuthContent
              networkId={selectedNetwork.networkId}
              networkName={selectedNetwork.name}
              onClose={handleClose}
              onBackToNetworks={() => {
                setSelectedNetwork(null);
                setView('list');
              }}
            />
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default PrivateNetworksDrawer;
